import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { OptionKey } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_OPTION, DEFAULT_DATE_FORMAT, TIME_FORMAT } from '@/common/constants';
import { Pagination, ValuesMap } from '@/common/types';
import { API_URL, ICommunicationDto, ILetterCommunicationDestination } from '@/dtos';

import { IResendItem, ResendItemStatus, ResendItemType } from '../ResendSection.types';

export const QUERY_KEY = 'GET_COMMUNICATIONS_RESEND';

interface ResponseCommunicationStatus {
  datetime: string;
  notification_status: ResendItemStatus;
}
export interface ViewHTML {
  html_template: string;
  template_variables: string;
}
export interface ViewLetter {
  url: string;
}
export interface Response {
  id: number;
  datetime: string;
  subject: string;
  is_resendable: boolean;
  sent_to: string;
  destination: {
    sent_to: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  };
  communication_type: ResendItemType;
  notification_status: ResendItemStatus;
  view: ViewLetter | ViewHTML;
  communication_statuses: ResponseCommunicationStatus[];
  page: number;
  perPage: number;
}

interface Params {
  employeeId: string;
  search: string;
  communicationTypes: OptionKey[];
  notificationStatuses: OptionKey[];
  startDate?: Date | null;
  endDate?: Date | null;
  page: number;
  perPage: number;
}

const useResendListQuery = ({
  employeeId,
  search,
  communicationTypes,
  notificationStatuses,
  startDate,
  endDate,
  page,
  perPage,
}: Params) => {
  const currentPage = useMemo(() => {
    return page <= 1 ? 1 : page;
  }, [page]);

  const { data, isLoading, isSuccess } = useQuery(
    [
      QUERY_KEY,
      employeeId,
      search,
      currentPage,
      perPage,
      startDate,
      endDate,
      ...communicationTypes,
      ...notificationStatuses,
    ],
    () => {
      const filteredTypes = communicationTypes.filter((item) => item !== ALL_OPTION.value);
      const filteredStatuses = notificationStatuses.filter((item) => item !== ALL_OPTION.value);
      return api.get<Pagination<ICommunicationDto>>(API_URL.COMMUNICATIONS, {
        params: _.pickBy({
          employeeId,
          search,
          communication_types: filteredTypes.join(','),
          notification_statuses: filteredStatuses.join(','),
          start_date: startDate ? dayjs(startDate).format(DEFAULT_DATE_FORMAT) : undefined,
          end_date: endDate ? dayjs(endDate).format(DEFAULT_DATE_FORMAT) : undefined,
          page: `${currentPage - 1}`,
          size: `${perPage}`,
        }),
      });
    }
  );
  const formattedData: IResendItem[] = useMemo(() => {
    const list = data?.data?.content || [];
    const formattedList: IResendItem[] = list.map((item) => {
      const view: string = _.get(item.view, 'url', '');
      const destination: ILetterCommunicationDestination =
        item.destination as ILetterCommunicationDestination;

      const newItem: IResendItem = {
        id: `${item.id}`,
        date: dayjs(item.datetime).format(DEFAULT_DATE_FORMAT),
        time: dayjs(item.datetime).format(TIME_FORMAT),
        subject: item.subject || '',
        isResendable: item.is_resendable || false,

        destination: {
          sentTo: destination?.sent_to || '',
          city: destination?.city,
          addressLine1: destination?.address_line1,
          addressLine2: destination?.address_line2,
          zipCode: destination?.zip_code,
          state: destination?.state,
        },
        type: item.communication_type as unknown as ResendItemType,
        status: item.notification_status as unknown as ResendItemStatus,
        view,
        templateId: _.toString(_.get(item.view, 'template_id', '')),
        templateVariables: _.get(item.view, 'template_variables', {}) as ValuesMap<string | number | boolean>,
        historyList: item.communication_statuses
          ? item.communication_statuses.map((history, index) => ({
              id: `${item.id}_${index}`,
              datetime: history.datetime as unknown as string,
              status: history.notification_status as unknown as ResendItemStatus,
            }))
          : [],
      };
      return newItem;
    });
    return formattedList;
  }, [data?.data?.content]);

  const total = useMemo(() => {
    return data?.data.totalElements || 0;
  }, [data?.data.totalElements]);

  return {
    total,
    data: formattedData,
    isLoading,
    isSuccess,
  };
};

export default useResendListQuery;
